/*
 * Note card
 * Purpose: Present note summaries, linked-state badges, and note-specific utility actions.
 * Implementation notes: Task creation remains in the note editor rather than being duplicated on every card.
 * Recent changes: Removed the redundant per-note Create Task checkmark action.
 */

import { FC } from 'react';
import { Note, NOTE_TYPES } from '../../models/Note';
import { THEME_CLASSES } from '../../constants/themeConstants';
import { highlightText } from '../../utils/highlightUtils';
import { openNoteInGmail, openNoteInCalendar } from '../../utils/googleUtils';
import NewsBadge from './NewsBadge';
import TasksBadge from './TasksBadge';
import { Avatar, Button, Surface } from '../primitives';
import { getNoteTypeBadgeClassName } from './noteTypeBadgeUtils';

interface NoteCardProps {
  note: Note;
  isSelected: boolean;
  onClick: () => void;
  searchQuery: string;
  onDeleteClick?: (note: Note) => void;
  hasLinkedTasks?: boolean;
}

const NoteCard: FC<NoteCardProps> = ({ note, isSelected, onClick, searchQuery, onDeleteClick, hasLinkedTasks = false }) => {
  const typeBadgeClassName = getNoteTypeBadgeClassName(note.type);

  return (
    <Surface
      variant="interactive"
      onClick={onClick}
      className={`p-4 cursor-pointer mb-2 mx-2
        ${isSelected ? 'bg-blue-50/80 dark:bg-blue-900/30' : THEME_CLASSES.hover.background}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Avatar name={note.author || 'Unknown'} size="sm" className="bg-red-400" />
          <div className={`text-sm font-medium ${THEME_CLASSES.text.primary}`}>
            {note.author ? highlightText(note.author, searchQuery ? searchQuery.split(/\s+/).filter(k => k.length > 0) : []) : 'Unknown'}
            <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">#{note.id}</span>
          </div>
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap ml-2">
          {new Date(note.updatedAt ?? note.createdAt).toLocaleString(undefined, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric'
          })}
        </span>
      </div>
      <div className={`text-xs font-medium ${THEME_CLASSES.text.primary} truncate mb-2`}>
        {highlightText(note.content?.split('\n')[0] ?? '', searchQuery ? searchQuery.split(/\s+/).filter(k => k.length > 0) : [])}
      </div>
      <div className={`text-xs ${THEME_CLASSES.text.secondary} line-clamp-2 whitespace-pre-line mb-2`}>
        {highlightText(note.content?.split('\n').slice(1).join('\n') ?? '', searchQuery ? searchQuery.split(/\s+/).filter(k => k.length > 0) : [])}
      </div>
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {note.type && NOTE_TYPES.map(t => t.toLowerCase()).includes(note.type.trim().toLowerCase()) && typeBadgeClassName && (
            <span className={`px-2 py-0.5 text-xs rounded-full ${typeBadgeClassName}`}>
              {note.type}
            </span>
          )}

          <NewsBadge note={note} />
          <TasksBadge note={note} hasLinkedTasks={hasLinkedTasks} showTaskFlag={false} />
        </div>

        <div className="flex gap-2">
          <Button
            onClick={(e) => {
              e.stopPropagation();
              openNoteInGmail(note, ''); // Open with empty recipient
            }}
            variant="icon"
            className="h-8 w-8"
            title="Compose Gmail"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </Button>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              // Default start time to now rounded to nearest hour
              const startDate = new Date();
              startDate.setMinutes(0, 0, 0);
              startDate.setHours(startDate.getHours() + 1);

              openNoteInCalendar(note, startDate);
            }}
            variant="icon"
            className="h-8 w-8"
            title="Add to Google Calendar"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </Button>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick?.(note);
            }}
            variant="icon"
            className="h-8 w-8"
            title="Delete note"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </Button>
        </div>
      </div>
    </Surface>
  );
};

export default NoteCard;
